import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Api, { formatUrl } from '@plone/volto/helpers/Api/Api';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { Message } from 'semantic-ui-react';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { lerResultadoSalvo } from 'volto-ministerio-publico-federal/helpers/AccessibilityAudit';
import { useA11yConfig } from 'volto-ministerio-publico-federal/helpers/AccessibilityAudit/config';

/**
 * Painel de auditoria de acessibilidade exibido na tela de edição.
 *
 * A auditoria roda AUTOMATICAMENTE no salvamento (add-on v2inclusiva.a11y, evento
 * padrão do Plone) e grava o veredito no conteúdo. Este painel mostra esse
 * resultado do último salvamento e mantém o botão "Verificar" como
 * PRÉ-VISUALIZAÇÃO: audita o rascunho da tela via `@a11y-auditar-rascunho`, sem
 * gravar nada (o veredito oficial é sempre o do salvamento). É renderizado apenas
 * para os tipos configurados como verificados (`@a11y-config`).
 */

// O `mod` casa com os modificadores de `.mpf-a11y-pill` / `.mpf-a11y-level`
// em `theme/styles/components/internal/_a11y.scss`; as cores vêm dos tokens
// do tema (e do alto contraste), não de hex fixo.
const FAIXA = {
  aprovado: { label: 'Aprovado', mod: 'aprovado' },
  com_ressalva: { label: 'Com ressalva', mod: 'ressalva' },
  bloqueado: { label: 'Bloqueado', mod: 'bloqueado' },
  bloqueado_grave: { label: 'Bloqueado (grave)', mod: 'bloqueado' },
};

// Níveis de conformidade (idem ao backend). Toleramos os valores legados
// 1A/2A/3A de auditorias antigas.
const NIVEL = {
  grave: { label: 'Grave', mod: 'grave' },
  moderado: { label: 'Moderado', mod: 'moderado' },
  baixo: { label: 'Baixo', mod: 'baixo' },
  '1A': { label: 'Grave', mod: 'grave' },
  '2A': { label: 'Moderado', mod: 'moderado' },
  '3A': { label: 'Baixo', mod: 'baixo' },
};

const STATUS = {
  conforme: { label: 'Conforme', mod: 'aprovado' },
  pendente: { label: 'Pendente', mod: 'ressalva' },
};

const pillClass = (mod) => `mpf-a11y-pill mpf-a11y-pill--${mod}`;
const levelClass = (mod) => `mpf-a11y-level mpf-a11y-level--${mod}`;

function formatPrazo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

const AccessibilityAuditPanel = ({
  pathname,
  content,
  type,
  isNew = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Rascunho NÃO salvo do editor. O <Form global> sincroniza o estado do
  // formulário (title/description/blocks/blocks_layout) para ``state.form.global``
  // a cada mudança, então aqui temos exatamente o que está na tela — mesmo sem
  // salvar. Cai para ``{}`` (audita o conteúdo salvo) se ainda não houver dado.
  const draftData = useSelector((state) => state.form?.global);

  const handleAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = new Api();
      // Pré-visualização: audita o rascunho da tela sem gravar nada. O veredito
      // que vale (nota, prazo, bloqueio) é o do salvamento automático.
      const url = `${formatUrl(getBaseUrl(pathname))}/@a11y-auditar-rascunho`;
      const res = await api.post(url, { data: draftData || {} });
      setResult(res);
    } catch (err) {
      const body = err?.response?.body;
      const message =
        body?.message ||
        body?.error?.message ||
        err?.message ||
        'Não foi possível auditar o conteúdo. Verifique se o serviço de acessibilidade está ativo.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [pathname, draftData]);

  // Tipos configurados no painel (sem lista fixa no frontend).
  const { porId, loaded, temTipo } = useA11yConfig();

  // Na criação o tipo vem via prop (não existe ``content`` carregado); na
  // edição o tipo é o ``@type`` do conteúdo salvo. Só renderiza com a
  // configuração carregada e para tipos verificados.
  const contentType = type || content?.['@type'];
  if (!pathname || !contentType || !loaded || !temTipo(contentType)) {
    return null;
  }
  // Tipo governado (notícia): prazo, bloqueio e despublicação. Os demais
  // apenas verificam.
  const governado = porId(contentType)?.governado ?? true;

  // Resultado do último salvamento (auditoria automática). Na criação ainda não
  // existe conteúdo salvo.
  const saved = isNew ? null : lerResultadoSalvo(content);
  const preview = Boolean(result);
  // A pré-visualização, quando existe, tem prioridade na tela.
  const shown = result || saved;
  const faixa = shown?.faixa ? FAIXA[shown.faixa] : null;
  const savedStatusToken =
    typeof content?.a11y_status === 'object'
      ? content?.a11y_status?.token
      : content?.a11y_status;
  const currentStatus =
    !isNew && savedStatusToken ? STATUS[savedStatusToken] : null;
  const issues = shown?.issues || [];
  const score = typeof shown?.score === 'number' ? shown.score : null;
  const prazo = shown?.a11y_prazo || saved?.a11y_prazo || null;

  // Fração do anel: o CSS monta o `conic-gradient` a partir de ``--a11y-score``
  // (o modificador de estado define a cor do arco).
  const ringStyle = { '--a11y-score': score != null ? score : 0 };
  const ringMod = faixa?.mod ? ` mpf-a11y-panel__ring--${faixa.mod}` : '';

  return (
    <section
      className="mpf-a11y-panel"
      aria-label="Auditoria de acessibilidade"
    >
      <div className="mpf-a11y-panel__head">
        <h3 className="mpf-a11y-panel__title">♿ Acessibilidade</h3>
        {!shown && currentStatus && (
          <span className={pillClass(currentStatus.mod)}>
            {currentStatus.label}
          </span>
        )}
        {shown && faixa && (
          <span className={pillClass(faixa.mod)}>{faixa.label}</span>
        )}
        <div className="mpf-a11y-panel__spacer" />
        <MPFButton
          variant="primary"
          size="m"
          loading={loading}
          disabled={loading}
          onClick={handleAudit}
          aria-label="Verificar acessibilidade"
        >
          {preview ? 'Verificar novamente' : 'Pré-visualizar verificação'}
        </MPFButton>
      </div>

      <div className="mpf-a11y-panel__body">
        {!shown && !loading && (
          <p className="mpf-a11y-panel__intro">
            {isNew
              ? 'A acessibilidade é verificada automaticamente quando você salvar. Use o botão para pré-visualizar a nota antes de salvar.'
              : 'Este conteúdo ainda não tem verificação. Ela é feita automaticamente quando você salvar.'}
          </p>
        )}
        {shown && (
          <p className="mpf-a11y-panel__intro">
            {preview
              ? 'Pré-visualização do que está na tela (não salva). A nota oficial é a do salvamento.'
              : `Resultado da verificação automática do último salvamento${
                  shown.ultima_auditoria
                    ? ` (${new Date(shown.ultima_auditoria).toLocaleString('pt-BR')})`
                    : ''
                }.`}
          </p>
        )}

        {error && (
          <Message negative size="small">
            <Message.Header>Falha na verificação</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {shown && !error && (
          <>
            <div className="mpf-a11y-panel__verdict">
              <div
                className={`mpf-a11y-panel__ring${ringMod}`}
                style={ringStyle}
              >
                <div className="mpf-a11y-panel__ring-inner">
                  <div className="mpf-a11y-panel__score">
                    <span className="mpf-a11y-panel__score-num">
                      {score != null ? score.toFixed(1) : '—'}
                    </span>
                    <span className="mpf-a11y-panel__score-label">nota</span>
                  </div>
                </div>
              </div>
              <div className="mpf-a11y-panel__verdict-text">
                {faixa && (
                  <span className={`${pillClass(faixa.mod)} mb-1`}>
                    {faixa.label}
                  </span>
                )}
                <p className="mpf-a11y-panel__verdict-title">
                  {!governado
                    ? 'Verificação informativa'
                    : shown.can_publish
                      ? 'Publicação permitida'
                      : 'Publicação bloqueada'}
                </p>
                {!governado && (
                  <p className="mpf-a11y-panel__verdict-msg">
                    Este tipo não bloqueia a publicação.
                  </p>
                )}
                {shown.message && (
                  <p className="mpf-a11y-panel__verdict-msg">{shown.message}</p>
                )}
              </div>
            </div>

            <dl className="mpf-a11y-panel__meta">
              <dt className="mpf-a11y-panel__meta-label">Total de falhas</dt>
              <dd className="mpf-a11y-panel__meta-value">
                {shown.total_issues ?? issues.length}
              </dd>
              <dt className="mpf-a11y-panel__meta-label">Falhas graves</dt>
              <dd className="mpf-a11y-panel__meta-value">
                {
                  issues.filter((i) => i.nivel === 'grave' || i.nivel === '1A')
                    .length
                }
              </dd>
              <dt className="mpf-a11y-panel__meta-label">Falhas moderadas</dt>
              <dd className="mpf-a11y-panel__meta-value">
                {
                  issues.filter(
                    (i) => i.nivel === 'moderado' || i.nivel === '2A',
                  ).length
                }
              </dd>
              <dt className="mpf-a11y-panel__meta-label">Falhas baixas</dt>
              <dd className="mpf-a11y-panel__meta-value">
                {
                  issues.filter((i) => i.nivel === 'baixo' || i.nivel === '3A')
                    .length
                }
              </dd>
              {governado && (
                <>
                  <dt className="mpf-a11y-panel__meta-label">
                    Prazo de correção
                  </dt>
                  <dd className="mpf-a11y-panel__meta-value">
                    {formatPrazo(prazo)}
                  </dd>
                </>
              )}
              {shown.notificar_gestor !== undefined && (
                <>
                  <dt className="mpf-a11y-panel__meta-label">
                    Notificar gestor
                  </dt>
                  <dd className="mpf-a11y-panel__meta-value">
                    {shown.notificar_gestor ? 'sim' : 'não'}
                  </dd>
                </>
              )}
            </dl>

            {issues.length > 0 && (
              <>
                <p className="mpf-a11y-panel__issues-title">
                  Falhas encontradas
                </p>
                <div>
                  {issues.map((issue, idx) => {
                    const nivel = NIVEL[issue.nivel];
                    return (
                      <div
                        key={`${issue.code}-${idx}`}
                        className="mpf-a11y-panel__issue"
                      >
                        <span className={levelClass(nivel?.mod || 'baixo')}>
                          {nivel?.label || '—'}
                        </span>
                        <div className="mpf-a11y-panel__issue-msg">
                          {issue.message}{' '}
                          {issue.code && (
                            <span className="mpf-a11y-panel__issue-code">
                              ({issue.code})
                            </span>
                          )}
                        </div>
                        {issue.selector && (
                          <div className="mpf-a11y-panel__selector">
                            {issue.selector}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

AccessibilityAuditPanel.propTypes = {
  pathname: PropTypes.string.isRequired,
  content: PropTypes.shape({
    '@type': PropTypes.string,
    a11y_status: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  type: PropTypes.string,
  isNew: PropTypes.bool,
};

export default AccessibilityAuditPanel;
